import { QueryClient } from "@tanstack/react-query";
import { recordsKeys } from "./use-records";
import { sharedRecordsKeys } from "./use-shared-records";
import { projectRecordsKeys } from "./project-records-keys";
import { invalidateAskProjectHoverCache } from "@/lib/ask-project-hover-cache";

/**
 * Универсальная функция для инвалидации всех кэшей записи
 * Clean Architecture - Presentation Layer (инфраструктурная логика UI)
 *
 * @param queryClient - React Query клиент
 * @param recordId - ID записи
 * @param isPublic - является ли запись публичной
 * @returns Promise<void>
 */
export async function invalidateAllRecordCaches(
  queryClient: QueryClient,
  recordId: string,
  isPublic: boolean = false
): Promise<void> {
  try {
    // Инвалидируем детальную информацию записи
    // exact: false позволяет инвалидировать все версии
    // refetchType "active" only — "all" rehydrates inactive detail queries and
    // multiplies GET /insight/{id}/ after generation completes.
    await queryClient.invalidateQueries({
      queryKey: recordsKeys.detail(recordId),
      exact: false,
      refetchType: "active",
    });

    // Инвалидируем метаданные записи (available_versions)
    // Используем префикс без include[] чтобы инвалидировать все варианты
    await queryClient.invalidateQueries({
      queryKey: [...recordsKeys.all, "meta", recordId],
      exact: false,
      refetchType: "active",
    });

    // Инвалидируем публичные записи если нужно
    if (isPublic) {
      await queryClient.invalidateQueries({
        queryKey: sharedRecordsKeys.detail(recordId),
        exact: false,
        refetchType: "active",
      });
    }

    // Инвалидируем списки записей проектов для Ask Project hover.
    await queryClient.invalidateQueries({
      queryKey: projectRecordsKeys.all,
      exact: false,
      refetchType: "active",
    });

    // Project ID в этом контексте неизвестен, очищаем весь hover-кэш.
    invalidateAskProjectHoverCache();
  } catch (error) {
    throw error;
  }
}
